import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Grid, Box, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveRtlIcon from '@mui/icons-material/DriveFileMoveRtl';

import ViewBar from './ViewBar';
import VotingSystem from '../components/VotingSystem';
import { collection, getDocs, addDoc, doc, deleteDoc, setDoc } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { firebaseConfig } from '../firebaseConfig';
import { initializeApp } from 'firebase/app';

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

// Function to get all marketing plans
const getMarketingPlans = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'marketingPlan'));
    const plans: any[] = [];
    
    querySnapshot.forEach((doc) => {
      plans.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return plans;
  } catch (error) {
    console.error('Error getting marketing plans:', error);
    return [];
  }
};

// Function to create a new marketing plan
const createNewMarketingPlan = async (planName: string, startDate: Date, endDate: Date) => {
  try {
    const planRef = await addDoc(collection(db, 'marketingPlan'), {
      name: planName,
      description: '',
      expectedBudget: 0,
      status: 'open',
      expectedScope: 'mid',
      expectedReturn: 'mid',
      owner: '',
      agency: '',
      files: [],
      createdAt: new Date(),
      updatedAt: new Date()
    });

    return planRef.id;
  } catch (error) {
    console.error('Error creating new marketing plan:', error);
    return null;
  }
};

// Function to delete a marketing plan
const deleteMarketingPlan = async (planId: string) => {
  try {
    const planRef = doc(db, 'marketingPlan', planId);
    await deleteDoc(planRef);

    console.log(`Marketing plan ${planId} deleted successfully`);
    return true;
  } catch (error) {
    console.error('Error deleting marketing plan:', error);
    return false;
  }
};

// Function to move a marketing plan back to bucket list
const moveToBucketList = async (planData: any, planId: string) => {
  try {
    // Create the bucket document
    const bucketData = {
      name: planData.name,
      description: planData.description,
      expectedBudget: planData.expectedBudget || 0,
      status: planData.status || 'open',
      expectedScope: planData.expectedScope || 'mid',
      expectedReturn: planData.expectedReturn || 'mid',
      files: planData.files || [],
      urls: planData.urls || [],
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Add to bucket list collection
    const bucketRef = await addDoc(collection(db, 'marketingBucketList'), bucketData);

    // Delete from marketing plan collection
    const planRef = doc(db, 'marketingPlan', planId);
    await deleteDoc(planRef);

    console.log(`Marketing plan ${planId} moved to bucket list ${bucketRef.id} successfully`);
    return bucketRef.id;
  } catch (error) {
    console.error('Error moving plan to bucket list:', error);
    return null;
  }
};

interface AllMarketingPlansProps {
  onPlanSelect: (planId: string, planName: string) => void;
}

const AllMarketingPlans: React.FC<AllMarketingPlansProps> = ({ onPlanSelect }) => {
  const [marketingPlans, setMarketingPlans] = useState<any[]>([]);
  const [showCreatePlan, setShowCreatePlan] = useState(false);
  const [newPlanName, setNewPlanName] = useState('');
  const [newPlanDescription, setNewPlanDescription] = useState('');
  const [newPlanExpectedBudget, setNewPlanExpectedBudget] = useState('');
  const [newPlanExpectedScope, setNewPlanExpectedScope] = useState('mid');
  const [newPlanExpectedReturn, setNewPlanExpectedReturn] = useState('mid');
  const [newPlanOwner, setNewPlanOwner] = useState('');
  const [newPlanAgency, setNewPlanAgency] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState<{ id: string; name: string } | null>(null);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [planToMove, setPlanToMove] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'vertical'>('grid');
  const [statusFilter, setStatusFilter] = useState('');
  const [scopeFilter, setScopeFilter] = useState('');
  const [returnFilter, setReturnFilter] = useState('');

  const handleDeletePlan = async () => {
    if (!planToDelete) return;
    
    setLoading(true);
    const success = await deleteMarketingPlan(planToDelete.id);
    if (success) {
      setDeleteDialogOpen(false);
      setPlanToDelete(null);
      await loadMarketingPlans(); // Reload plans
    }
    setLoading(false);
  };

  const openDeleteDialog = (planId: string, planName: string) => {
    setPlanToDelete({ id: planId, name: planName });
    setDeleteDialogOpen(true);
  };

  const openMoveDialog = (plan: any) => {
    setPlanToMove(plan);
    setMoveDialogOpen(true);
  };

  const handleMoveToBucketList = async () => {
    if (!planToMove) return;
    
    setLoading(true);
    const bucketId = await moveToBucketList(planToMove, planToMove.id);
    if (bucketId) {
      setMoveDialogOpen(false);
      setPlanToMove(null);
      await loadMarketingPlans(); // Reload plans
    }
    setLoading(false);
  };

  const handleCreatePlan = async () => {
    if (!newPlanName.trim()) return;
    
    setLoading(true);
    const planData = {
      name: newPlanName,
      description: newPlanDescription,
      expectedBudget: parseFloat(newPlanExpectedBudget) || 0,
      expectedScope: newPlanExpectedScope,
      expectedReturn: newPlanExpectedReturn,
      owner: newPlanOwner,
      agency: newPlanAgency,
      status: 'open',
      files: []
    };
    
    const planId = await createNewMarketingPlan(newPlanName, new Date(), new Date());
    if (planId) {
      setNewPlanName('');
      setNewPlanDescription('');
      setNewPlanExpectedBudget('');
      setNewPlanExpectedScope('mid');
      setNewPlanExpectedReturn('mid');
      setNewPlanOwner('');
      setNewPlanAgency('');
      setShowCreatePlan(false);
      await loadMarketingPlans(); // Reload plans
      onPlanSelect(planId, newPlanName); // Select the new plan
    }
    setLoading(false);
  };

  // Load marketing plans
  const loadMarketingPlans = async () => {
    try {
      const plans = await getMarketingPlans();
      console.log('Loaded plans:', plans);
      setMarketingPlans(plans);
    } catch (error) {
      console.error('Error loading marketing plans:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load data from Firebase on component mount
  useEffect(() => {
    loadMarketingPlans();
  }, []);

  const handlePlanSelect = (planId: string, planName: string) => {
    console.log('Plan selected:', planId, planName);
    const displayName = planName || `Plan ${planId}`;
    onPlanSelect(planId, displayName);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in progress': return 'warning';
      case 'open': return 'default';
      default: return 'default';
    }
  };

  const getScopeColor = (scope: string) => {
    switch (scope) {
      case 'high': return 'error';
      case 'mid': return 'warning';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const getReturnColor = (returnValue: string) => {
    switch (returnValue) {
      case 'high': return 'success';
      case 'mid': return 'warning';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Filter marketing plans based on selected filters
  const filteredMarketingPlans = marketingPlans.filter(plan => {
    const statusMatch = !statusFilter || plan.status === statusFilter;
    const scopeMatch = !scopeFilter || plan.expectedScope === scopeFilter;
    const returnMatch = !returnFilter || plan.expectedReturn === returnFilter;
    return statusMatch && scopeMatch && returnMatch;
  });

  if (loading) {
    return (
      <div className="bg-gray-100 text-gray-800 min-h-screen flex items-center justify-center">
        <Typography variant="h6">Loading marketing plans...</Typography>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 text-gray-800 min-h-screen">
      <ViewBar 
        view={viewMode} 
        onViewChange={setViewMode} 
        onCreateNew={() => setShowCreatePlan(true)}
        createButtonText="Create New Plan"
        showFilters={true}
        statusFilter={statusFilter}
        scopeFilter={scopeFilter}
        returnFilter={returnFilter}
        onStatusFilterChange={setStatusFilter}
        onScopeFilterChange={setScopeFilter}
        onReturnFilterChange={setReturnFilter}
        statusOptions={[
          { value: 'open', label: 'Open' },
          { value: 'in progress', label: 'In Progress' },
          { value: 'completed', label: 'Completed' }
        ]}
      />
      <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8" style={{ marginTop: '60px' }}>
        <Grid container spacing={3} sx={{
          ...(viewMode === 'vertical' && {
            flexDirection: 'column',
            maxWidth: '800px',
            margin: '0 auto',
            '& .MuiGrid-item': {
              maxWidth: '100%',
              flexBasis: '100%'
            }
          })
        }}>
          {filteredMarketingPlans.map((plan) => (
            <Grid item xs={12} sm={6} md={4} key={plan.id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                  }
                }}
                onClick={() => handlePlanSelect(plan.id, plan.name)}
              >
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', flex: 1 }}>
                        {plan.name || `Plan ${plan.id}` } 
                        <span style={{ fontSize: '0.75rem', color: '#666', fontWeight: 'normal', paddingLeft: '8px' }}>
                          ({plan.files?.length || 0} files)
                        </span>
                      </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openMoveDialog(plan);
                        }}
                        sx={{ 
                          color: 'warning.main',
                          '&:hover': { backgroundColor: 'warning.light', color: 'white' }
                        }}
                        title="Move to Bucket List"
                      >
                        <DriveFileMoveRtlIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteDialog(plan.id, plan.name);
                        }}
                        sx={{ 
                          color: 'error.main',
                          '&:hover': { backgroundColor: 'error.light', color: 'white' }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Description */}
                  {plan.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {plan.description.length > 250 
                        ? `${plan.description.substring(0, 250)}...` 
                        : plan.description
                      }
                    </Typography>
                  )}

                  {/* Budget */}
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Budget
                    </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {formatCurrency(plan.expectedBudget || 0)}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Owner
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {plan.owner || 'Not assigned'}
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Agency
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {plan.agency || 'Not assigned'}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <Chip 
                      label={plan.status || 'open'} 
                      color={getStatusColor(plan.status)} 
                      size="small" 
                    />
                    <Chip 
                      label={`Scope: ${plan.expectedScope || 'mid'}`} 
                      color={getScopeColor(plan.expectedScope)} 
                      size="small" 
                    />
                    <Chip 
                      label={`Return: ${plan.expectedReturn || 'mid'}`} 
                      color={getReturnColor(plan.expectedReturn)} 
                      size="small" 
                    />
                  </Box>

                  {/* Votes */}
                  <Box sx={{ mt: 'auto' }}>
                    <VotingSystem 
                      collectionName="marketingPlan" 
                      documentId={plan.id} 
                      size="small"
                      showCounts={true}
                      showTooltips={true}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {filteredMarketingPlans.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Marketing Plans Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first marketing plan to get started.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreatePlan(true)}
              sx={{ backgroundColor: '#4f46e5', '&:hover': { backgroundColor: '#4338ca' } }}
            >
              Create New Plan
            </Button>
          </Box>
        )}

        {/* Create Plan Dialog */}
        <Dialog open={showCreatePlan} onClose={() => setShowCreatePlan(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create New Marketing Plan</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <TextField
                label="Plan Name"
                value={newPlanName}
                onChange={(e) => setNewPlanName(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
                required
              />
              <TextField
                label="Description"
                value={newPlanDescription}
                onChange={(e) => setNewPlanDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Expected Budget"
                type="number"
                value={newPlanExpectedBudget}
                onChange={(e) => setNewPlanExpectedBudget(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>$</Typography>
                }}
              />
              <TextField
                label="Owner"
                value={newPlanOwner}
                onChange={(e) => setNewPlanOwner(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              />
              <TextField
                label="Agency"
                value={newPlanAgency}
                onChange={(e) => setNewPlanAgency(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              />
              {/* <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    select
                    label="Expected Scope"
                    value={newPlanExpectedScope}
                    onChange={(e) => setNewPlanExpectedScope(e.target.value)}
                    fullWidth
                  >
                    <option value="low">Low</option>
                    <option value="mid">Mid</option>
                    <option value="high">High</option>
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    select
                    label="Expected Return"
                    value={newPlanExpectedReturn}
                    onChange={(e) => setNewPlanExpectedReturn(e.target.value)}
                    fullWidth
                  >
                    <option value="low">Low</option>
                    <option value="mid">Mid</option>
                    <option value="high">High</option>
                  </TextField>
                </Grid>
              </Grid> */}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCreatePlan(false)}>Cancel</Button>
            <Button 
              onClick={handleCreatePlan} 
              variant="contained"
              disabled={!newPlanName.trim()}
            >
              Create Plan
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Marketing Plan</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{planToDelete?.name}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDeletePlan} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Move to Bucket List Confirmation Dialog */}
        <Dialog
          open={moveDialogOpen}
          onClose={() => {
            setMoveDialogOpen(false);
            setPlanToMove(null);
          }}
          aria-labelledby="move-dialog-title"
        >
          <DialogTitle id="move-dialog-title">
            Move to Bucket List
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to move "{planToMove?.name}" from the marketing plan to the bucket list?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setMoveDialogOpen(false);
                setPlanToMove(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleMoveToBucketList}
              color="warning"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Moving...' : 'Move to Bucket List'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default AllMarketingPlans; 